import axios from 'axios';
import { API_URL, API_KEY, ENDPOINTS } from '../lib/constants';
import type {
  QuickCompareQueryResult,
  QuickCompareStore,
  ComplianceFinding,
} from '../types/quickCompare';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 300000, // 5 minutes timeout for file uploads
  headers: {
    'x-api-key': API_KEY,
    // Don't set Content-Type here - let axios set it automatically based on data type
    // For FormData, axios will set multipart/form-data with boundary
    // For JSON, axios will set application/json
  },
});

// Create RAG store
export const createRagStore = async (
  displayName: string,
  ragService: 'gemini' | 'langgraph' = 'gemini',
  userId?: string,
  orgId?: string
): Promise<string> => {
  try {
    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
    };

    // Add user context headers if provided
    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.post<{ storeName: string }>(
      ENDPOINTS.QUICK_COMPARE_CREATE_STORE,
      { displayName, ragService },
      { headers }
    );
    return response.data.storeName;
  } catch (error) {
    console.error('Error creating RAG store:', error);
    throw error;
  }
};

// Upload file to RAG store
export const uploadToRagStore = async (
  storeId: string,
  file: File,
  fileType: 'policy' | 'standard',
  ragService: 'gemini' | 'langgraph' = 'gemini',
  userId?: string,
  orgId?: string,
  options?: {
    chunkingConfig?: {
      maxTokensPerChunk?: number;
      maxOverlapTokens?: number;
    };
    customMetadata?: Array<{
      key: string;
      stringValue?: string;
      numericValue?: number;
    }>;
  }
): Promise<{ markdownUrl?: string; content?: string; conversionSource?: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('storeId', storeId);
    formData.append('fileType', fileType);
    formData.append('ragService', ragService);

    // Add chunking configuration if provided
    if (options?.chunkingConfig) {
      formData.append('chunkingConfig', JSON.stringify(options.chunkingConfig));
    }

    // Add custom metadata if provided
    if (options?.customMetadata && options.customMetadata.length > 0) {
      formData.append('customMetadata', JSON.stringify(options.customMetadata));
    }

    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
      // Don't set Content-Type - axios will automatically set multipart/form-data with boundary for FormData
    };

    // Add user context headers if provided
    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.post(ENDPOINTS.QUICK_COMPARE_UPLOAD, formData, {
      headers,
      timeout: 300000, // 5 minutes for large files
    });
    return response.data;
  } catch (error: any) {
    // Check if store doesn't exist (404 error)
    if (error?.response?.status === 404 && error?.response?.data?.error === 'Store does not exist') {
      throw new Error('STORE_NOT_FOUND');
    }
    console.error('Error uploading file to RAG store:', error);
    throw error;
  }
};

// Query documents
export const queryDocuments = async (
  storeId: string,
  query: string,
  systemPrompt?: string,
  ragService: 'gemini' | 'langgraph' = 'gemini',
  userId?: string,
  orgId?: string
): Promise<QuickCompareQueryResult> => {
  try {
    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
    };

    // Add user context headers if provided (needed for Supabase RLS)
    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.post<QuickCompareQueryResult>(
      ENDPOINTS.QUICK_COMPARE_QUERY,
      { storeId, query, systemPrompt, ragService },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error querying documents:', error);
    throw error;
  }
};

// Generate example questions
export const generateExampleQuestions = async (
  storeId: string
): Promise<string[]> => {
  try {
    const response = await api.post<{ questions: string[] }>(
      ENDPOINTS.QUICK_COMPARE_GENERATE_QUESTIONS,
      { storeId }
    );
    return response.data.questions;
  } catch (error) {
    console.error('Error generating example questions:', error);
    throw error;
  }
};

// List files in RAG store
export const listFilesInStore = async (
  storeId: string,
  category?: 'policy' | 'standard',
  ragService: 'gemini' | 'langgraph' = 'gemini',
  userId?: string,
  orgId?: string
): Promise<Array<{ name: string; category: 'policy' | 'standard' }>> => {
  try {
    // URL encode the storeId to handle slashes in the resource name
    const encodedStoreId = encodeURIComponent(storeId);
    let endpoint = ENDPOINTS.QUICK_COMPARE_LIST_FILES.replace(':id', encodedStoreId);
    const params = new URLSearchParams();
    if (category) {
      params.append('category', category);
    }
    params.append('ragService', ragService);
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
    };

    // Add user context headers if provided (needed for Supabase RLS)
    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.get<{ files: Array<{ name: string; category: 'policy' | 'standard' }> }>(endpoint, { headers });
    return response.data.files;
  } catch (error: any) {
    // Handle network errors (server not running)
    if (
      error?.code === 'ERR_CONNECTION_REFUSED' ||
      error?.message?.includes('Network Error') ||
      error?.message?.includes('ERR_CONNECTION_REFUSED')
    ) {
      console.warn('Server not available, returning empty file list');
      throw new Error('SERVER_NOT_AVAILABLE');
    }

    // Handle store not found (404 or 500 with store error)
    if (
      error?.response?.status === 404 ||
      error?.response?.status === 500 ||
      (error?.response?.data?.error?.includes('does not exist') ||
        error?.response?.data?.error?.includes('PERMISSION_DENIED'))
    ) {
      console.warn('Store does not exist or permission denied');
      throw new Error('STORE_NOT_FOUND');
    }

    console.error('Error listing files in store:', error);
    throw error; // Re-throw other errors
  }
};

// Get file details including markdown URL and content
export interface FileDetails {
  name: string;
  category?: 'policy' | 'standard';
  markdownUrl?: string;
  filepath?: string;
  fileType?: string;
  fileSize?: number;
  markdownContent?: string;
}

export const getFileDetails = async (
  fileName: string,
  category?: 'policy' | 'standard',
  userId?: string,
  orgId?: string
): Promise<FileDetails | null> => {
  try {
    const params = new URLSearchParams();
    params.append('fileName', fileName);
    if (category) {
      params.append('category', category);
    }

    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
    };

    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.get<FileDetails>(
      `/api/revisr/quick-compare/file-details?${params.toString()}`,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching file details:', error);
    return null;
  }
};

// Delete RAG store
export const deleteRagStore = async (
  storeId: string,
  ragService: 'gemini' | 'langgraph' = 'gemini'
): Promise<void> => {
  try {
    // URL encode the storeId to handle slashes in the resource name
    const encodedStoreId = encodeURIComponent(storeId);
    const endpoint = `${ENDPOINTS.QUICK_COMPARE_DELETE_STORE.replace(':id', encodedStoreId)}?ragService=${ragService}`;
    await api.delete(endpoint);
  } catch (error) {
    console.error('Error deleting RAG store:', error);
    // Don't throw - cleanup errors shouldn't break the UI
  }
};

// Multi-agent comparison
export const compareMultipleStandards = async (
  policyId: string,
  standardIds: string[],
  criteria?: string,
  userId?: string,
  orgId?: string
): Promise<import('../types/quickCompare').MultiCompareResult> => {
  try {
    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
    };

    if (userId) {
      headers['x-user-id'] = userId;
    }
    if (orgId) {
      headers['x-org-id'] = orgId;
    }

    const response = await api.post<import('../types/quickCompare').MultiCompareResult>(
      ENDPOINTS.QUICK_COMPARE_MULTI_COMPARE,
      { policyId, standardIds, criteria },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error running multi-agent comparison:', error);
    throw error;
  }
};

// Streaming comparison
export const compareMultipleStandardsStream = async (
  policyId: string,
  standardIds: string[],
  onEvent: (event: any) => void,
  systemPrompt?: string,
  userId?: string,
  orgId?: string
): Promise<void> => {
  // Check if mock mode is enabled via environment variable
  const useMockStream = import.meta.env.VITE_USE_MOCK_STREAM === 'true';

  if (useMockStream) {
    console.log('🎭 Using mock stream data (VITE_USE_MOCK_STREAM=true)');

    // Simulate streaming with mock data
    const mockFindings: ComplianceFinding[] = [
      {
        originalText: 'Sample policy text that needs revision',
        revisedText: 'Revised policy text that complies with the standard',
        reference: 'Section 4.2',
        complianceStatus: 'non-compliant',
        severity: 'high',
        findingType: 'major',
        explanation: 'This is a mock finding for testing purposes',
      },
      {
        originalText: '',
        revisedText: 'New requirement that should be added',
        reference: 'Section 5.1',
        complianceStatus: 'missing',
        severity: 'medium',
        findingType: 'minor',
        explanation: 'This is a mock finding for testing purposes',
      },
    ];

    // Simulate step events
    onEvent({ type: 'step_start', step: 'init', message: 'Initializing mock comparison...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    onEvent({ type: 'step_start', step: 'analyze', message: 'Analyzing documents...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    // Simulate final report with mock findings
    const mockReport = JSON.stringify({
      findings: mockFindings,
      score: {
        totalRequirements: 10,
        compliantRequirements: 8,
        nonCompliantRequirements: 1,
        missingRequirements: 1,
        needsClarification: 0,
        scoreBreakdown: {
          critical: 0,
          high: 1,
          medium: 1,
          low: 0,
        },
        finalScore: 93,
        scoreExplanation: 'Mock score calculation',
      },
      hasFindings: true,
      message: 'Mock comparison complete with 2 findings',
    });

    onEvent({ type: 'final_report', report: mockReport });
    return;
  }

  const headers: Record<string, string> = {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json',
  };

  if (userId) headers['x-user-id'] = userId;
  if (orgId) headers['x-org-id'] = orgId;

  // Log what we're sending to the backend
  console.log('📤 Sending to backend streaming comparison:', {
    policyId,
    standardIds,
    standardCount: standardIds.length,
    hasSystemPrompt: !!systemPrompt,
    userId,
    orgId
  });

  const response = await fetch(`${API_URL}${ENDPOINTS.QUICK_COMPARE_MULTI_COMPARE_STREAM}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ policyId, standardIds, criteria: undefined, systemPrompt }),
  });

  if (!response.ok) {
    // Try to get error message from response body
    let errorMessage = `Streaming request failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error || errorData.message) {
        errorMessage = errorData.error || errorData.message;
      }
    } catch {
      // If response is not JSON, try to read as text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch {
        // If response is not text, use status text
      }
    }
    // Send error event before throwing
    onEvent({ type: 'error', message: errorMessage });
    throw new Error(errorMessage);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) return;

  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      // The last element is either empty (if buffer ended with \n\n) or the start of the next message
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonStr = line.substring(6);
            if (jsonStr.trim()) {
              const event = JSON.parse(jsonStr);
              // Check if the event itself contains an error
              if (event.type === 'error' || event.error) {
                onEvent({ type: 'error', message: event.error || event.message || 'An error occurred during streaming' });
                return;
              }
              onEvent(event);
            }
          } catch (e) {
            console.warn('Failed to parse SSE event:', e);
            // If we can't parse the event, it might be an error message
            const errorLine = line.substring(6).trim();
            if (errorLine.toLowerCase().includes('error') || errorLine.toLowerCase().includes('insufficient')) {
              onEvent({ type: 'error', message: errorLine });
              return;
            }
          }
        } else if (line.trim() && !line.startsWith('event:') && !line.startsWith('id:')) {
          // Check if the line itself is an error message
          const trimmedLine = line.trim();
          if (trimmedLine.toLowerCase().includes('error') || trimmedLine.toLowerCase().includes('insufficient')) {
            onEvent({ type: 'error', message: trimmedLine });
            return;
          }
        }
      }
    }
  } catch (error) {
    // Catch any errors during streaming
    const errorMsg = error instanceof Error ? error.message : 'An error occurred during streaming';
    onEvent({ type: 'error', message: errorMsg });
    throw error;
  }
};



// Finalize policy
export const finalizePolicy = async (
  markdown: string,
  changes: any[],
  policyName: string,
  userId?: string,
  orgId?: string,
  policyReviewId?: string
): Promise<{ finalMarkdownUrl: string; content: string }> => {
  try {
    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };

    if (userId) headers['x-user-id'] = userId;
    if (orgId) headers['x-org-id'] = orgId;

    const response = await api.post<{ finalMarkdownUrl: string; content: string }>(
      '/api/revisr/finalize-policy',
      { markdown, changes, policyName, policyReviewId },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error finalizing policy:', error);
    throw error;
  }
};

// Update policy markdown content directly
export const updatePolicyMarkdown = async (
  id: string,
  markdown: string,
  userId?: string,
  orgId?: string
): Promise<{ markdown_url: string }> => {
  try {
    const headers: Record<string, string> = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };

    if (userId) headers['x-user-id'] = userId;
    if (orgId) headers['x-org-id'] = orgId;

    const response = await api.patch<{ markdown_url: string; message: string }>(
      `/api/revisr/policy-review/${id}/markdown`,
      { markdown },
      { headers }
    );
    return { markdown_url: response.data.markdown_url };
  } catch (error) {
    console.error('Error updating policy markdown:', error);
    throw error;
  }
};
